/**
 * The vertex attributes the programs of the painted and water materials read, and how a built world
 * gives every mesh all of them (PLAN, painted material; round 14).
 *
 * WebGL keeps the value an attribute takes on a mesh without an array for it (the generic vertex
 * attribute) as context state, not state of the mesh's vertex array, and three writes a material's
 * defaultAttributeValues only when it first sets up a mesh's vertex array. The last value any
 * program left at a location is what every program reads there on every later frame: the tree
 * crowns read their painter scale where the trunks' program keeps its axis offset, and from the
 * second frame on drew at scale zero in the painter view. So these materials keep no default values,
 * and a mesh that leaves out an attribute its program reads carries it as an array instead.
 */

import {
  InstancedBufferAttribute,
  type BufferGeometry,
  type InstancedMesh,
  type Material,
  type Mesh,
  type Object3D,
} from 'three';
import { ANCHOR } from '../anchorCode';

/** What one program reads: attributes every mesh must carry, and attributes a mesh may leave out,
 *  each with the value that stands for leaving it out. */
export interface ProgramAttributes {
  readonly required: readonly string[];
  readonly optional: Readonly<Record<string, readonly number[]>>;
}

/** A material's programs: the one three builds for a mesh and, where the material draws instanced
 *  meshes, the one it builds for them. */
export interface MaterialAttributes {
  readonly mesh: ProgramAttributes;
  readonly instanced?: ProgramAttributes;
  readonly batched?: ProgramAttributes;
  /** A batch whose instances have colours: citizens, each posed by its own (painted.vert). */
  readonly citizens?: ProgramAttributes;
}

const NO_EXAGGERATION: readonly number[] = [ANCHOR.none, 0];

const PAINTED_MESH: ProgramAttributes = {
  required: ['position', 'normal', 'color', 'aFacade', 'aMeta'],
  optional: { aExaggeration: NO_EXAGGERATION, aPainterOffset: [0, 0, 0] },
};

export const PAINTED_ATTRIBUTES: MaterialAttributes = {
  mesh: PAINTED_MESH,
  batched: {
    required: PAINTED_MESH.required,
    optional: { aExaggeration: NO_EXAGGERATION },
  },
  citizens: {
    required: ['position', 'normal', 'color', 'aJoints', 'aWeights'],
    optional: { aExaggeration: NO_EXAGGERATION },
  },
  instanced: {
    required: [...PAINTED_MESH.required, 'aInstanceEdgeId'],
    optional: {
      aExaggeration: NO_EXAGGERATION,
      aPainterScale: [1, 1],
      aAxisOffset: [0, 0, 0],
      aNormalOffset: [0, 0, 0],
    },
  },
};

/** Cloth uses a separate variant: coloured instances already fill Safari's attribute budget. */
export const CLOTH_PAINTED_ATTRIBUTES: MaterialAttributes = {
  ...PAINTED_ATTRIBUTES,
  mesh: {
    ...PAINTED_ATTRIBUTES.mesh,
    optional: { ...PAINTED_ATTRIBUTES.mesh.optional, aClothWind: [0, 0, 0, 0] },
  },
  instanced: {
    ...PAINTED_ATTRIBUTES.instanced!,
    optional: { ...PAINTED_ATTRIBUTES.instanced!.optional, aClothWind: [0, 0, 0, 0] },
  },
};

export const HEARTH_SMOKE_ATTRIBUTES: MaterialAttributes = {
  ...PAINTED_ATTRIBUTES,
  instanced: {
    ...PAINTED_ATTRIBUTES.instanced!,
    required: [...PAINTED_ATTRIBUTES.instanced!.required, 'aSmokeOpacity'],
  },
};

export const WATER_ATTRIBUTES: MaterialAttributes = {
  mesh: {
    required: ['position', 'color', 'aMeta', 'aWaterKind'],
    optional: { aExaggeration: NO_EXAGGERATION },
  },
};

const KEY = 'programAttributes';

/** Records which attributes `material`'s programs read, and drops the default values three would
 *  otherwise write for attributes a mesh lacks. */
export function withProgramAttributes<M extends Material>(
  material: M,
  attributes: MaterialAttributes
): M {
  material.userData[KEY] = attributes;
  (material as { defaultAttributeValues?: object }).defaultAttributeValues = {};
  return material;
}

export function programAttributesOf(material: Material): MaterialAttributes | undefined {
  return material.userData[KEY] as MaterialAttributes | undefined;
}

interface Use {
  readonly geometry: BufferGeometry;
  readonly name: string;
  readonly program: ProgramAttributes;
  readonly instances: number;
}

function isMesh(object: Object3D): object is Mesh {
  return (object as Partial<Mesh>).isMesh === true;
}

function isInstancedMesh(mesh: Mesh): mesh is InstancedMesh {
  return (mesh as Partial<InstancedMesh>).isInstancedMesh === true;
}

function programFor(mesh: Mesh, material: Material): ProgramAttributes | null {
  const attributes = programAttributesOf(material);
  if (!attributes) return null;
  if ((mesh as Mesh & { isBatchedMesh?: boolean }).isBatchedMesh) {
    const posed = mesh.geometry.hasAttribute('aJoints') ? attributes.citizens : attributes.batched;
    if (posed) return posed;
    throw new Error(`${mesh.name || mesh.uuid}: its material is not drawn batched`);
  }
  if (!isInstancedMesh(mesh)) return attributes.mesh;
  if (attributes.instanced) return attributes.instanced;
  throw new Error(`${mesh.name || mesh.uuid}: its material is not drawn instanced`);
}

function usesOf(root: Object3D): Use[] {
  const uses: Use[] = [];
  root.traverse((object) => {
    if (!isMesh(object)) return;
    const instances = isInstancedMesh(object) ? object.instanceMatrix.count : 1;
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      const program = programFor(object, material);
      if (program) {
        uses.push({
          geometry: object.geometry,
          name: object.name || object.uuid,
          program,
          instances,
        });
      }
    }
  });
  return uses;
}

/* One element, stepped on once over every instance the mesh can draw, so every vertex of every
   instance reads it; a per-vertex copy would cost the ground and the houses megabytes. */
function constantAttribute(value: readonly number[], instances: number): InstancedBufferAttribute {
  return new InstancedBufferAttribute(Float32Array.from(value), value.length, false, instances);
}

/**
 * Gives every mesh under `root` each attribute its material's program reads and its geometry
 * leaves out, holding the value that stands for leaving it out. Throws, naming the mesh, where a
 * geometry lacks an attribute that has no such value.
 */
export function completeProgramAttributes(root: Object3D): void {
  const uses = usesOf(root);
  for (const { geometry, name, program } of uses) {
    const lacking = program.required.filter((attribute) => !geometry.hasAttribute(attribute));
    if (lacking.length > 0) throw new Error(`${name} lacks ${lacking.join(', ')}`);
  }
  const instances = new Map<BufferGeometry, number>();
  for (const use of uses) {
    instances.set(use.geometry, Math.max(instances.get(use.geometry) ?? 1, use.instances));
  }
  for (const { geometry, program } of uses) {
    for (const [attribute, value] of Object.entries(program.optional)) {
      if (!geometry.hasAttribute(attribute)) {
        geometry.setAttribute(attribute, constantAttribute(value, instances.get(geometry) ?? 1));
      }
    }
  }
}
